import { memo } from 'react';

import { cn } from '@veraclins-dev/utils';

import {
  type ListItemVariants,
  listItemVariants,
} from './utils/variants/list-item';
import { extractStyleProps } from './utils/variants/styles';

interface ListItemProps
  extends
    Omit<
      React.HTMLAttributes<HTMLElement>,
      keyof ListItemVariants | 'onSelect'
    >,
    ListItemVariants {
  /** The `<li>` element, for a parent that measures or moves the row. */
  ref?: React.Ref<HTMLLIElement>;
  /** The value this item represents (for selection) */
  value?: string;
  /**
   * Gives the row the `primary-muted` surface, for example an unread
   * notification. It changes no ARIA attribute, so assistive technology does
   * not announce the row as selected. `selected` wins when both are set.
   */
  highlighted?: boolean;
  /** Whether this item is currently selected */
  selected?: boolean;
  /** Whether this item is currently focused */
  focused?: boolean;
  /** Whether this item is disabled */
  disabled?: boolean;
  /** ARIA role for accessibility. Useful for 'option' in listbox. */
  role?: React.AriaRole;
  /**
   * Called with `value` when the item is clicked, unless it is disabled or a
   * click handler called `preventDefault`. It adds no keyboard path, so use it
   * on rows of a widget that owns the keyboard, such as `role="option"` in a
   * listbox. For a plain row that is one action, put a `<button>` or link
   * inside the item instead.
   */
  onSelect?: (value?: string) => void;
}

// Roles that carry `aria-selected` and a roving tab stop.
const ariaSelectedRoles = new Set<React.AriaRole>([
  'gridcell',
  'option',
  'row',
  'tab',
]);

/**
 * A list item component that supports various styling options and states.
 * This enables consistent list item styling across the application with a consistent API.
 */
function BaseListItem({
  className,
  variant,
  itemSize,
  weight,
  tone,
  highlighted = false,
  selected = false,
  focused = false,
  disabled = false,
  value,
  role,
  onSelect,
  children,
  ...props
}: ListItemProps) {
  const { styleProps, others } = extractStyleProps(props);
  const {
    'aria-selected': ariaSelected,
    onClick,
    tabIndex,
    ...listItemProps
  } = others;
  const supportsSelectionAria = role ? ariaSelectedRoles.has(role) : false;

  return (
    // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- onSelect rows sit in a widget (listbox) that owns the keyboard
    <li
      data-slot="list-item"
      role={role}
      aria-selected={
        supportsSelectionAria ? (ariaSelected ?? selected) : undefined
      }
      tabIndex={
        supportsSelectionAria ? (tabIndex ?? (focused ? 0 : -1)) : tabIndex
      }
      className={cn(
        listItemVariants({
          variant,
          itemSize,
          weight,
          tone,
          highlighted,
          selected,
          focused,
          disabled,
          ...styleProps,
          className,
        }),
      )}
      onClick={
        onSelect
          ? (event) => {
              onClick?.(event);
              if (!event.defaultPrevented && !disabled) onSelect(value);
            }
          : onClick
      }
      {...listItemProps}
    >
      {children}
    </li>
  );
}

/**
 * A memoized list item component that supports various styling options and states.
 * This enables consistent list item styling across the application with a consistent API.
 * It is an `<li>`: for a row that is one action, put a `Button` or link inside
 * it.
 * @param {ListItemProps} props - The props for the ListItem component.
 * @returns {JSX.Element} A rendered HTML element with applied styles and children.
 * @example
 * ```tsx
 * <ListItem variant="clickable" itemSize="md" tone="danger">
 *   Interactive list item
 * </ListItem>
 * ```
 * @example
 * A notification row. `highlighted` marks it unread without announcing it as selected.
 * ```tsx
 * <ListItem highlighted>
 *   <Button emphasis="ghost" fullWidth className="justify-start gap-3" onClick={open}>
 *     <span className="flex size-9 items-center justify-center rounded-md bg-primary text-primary-foreground">
 *       <Icon name="bell" />
 *     </span>
 *     <span className="flex-1 text-start">New comment on your post</span>
 *     <span aria-hidden="true" className="size-2 rounded-full bg-primary" />
 *   </Button>
 * </ListItem>
 * ```
 * @subcomponentOf List
 */
const ListItem = memo(BaseListItem);

export { ListItem, type ListItemProps };
export default BaseListItem;
